import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { errMsg, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileUp, ShieldCheck, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success(t("Perfil actualizado"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/id-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser({ ...user, has_id_document: true });
      toast.success(t("Documento de identidad subido correctamente"));
      setFile(null);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12" data-testid="profile-page">
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-10">{t("Mi perfil")}</h1>

      <div className="card-tech rounded-xl p-8 mb-6">
        <form onSubmit={save} className="space-y-5" data-testid="profile-form">
          <div>
            <Label className="text-slate-300">{t("Correo electrónico")}</Label>
            <Input value={user?.email || ""} disabled className="mt-1.5 bg-[#0B0F1A]/50 border-white/10 text-slate-500" />
          </div>
          <div>
            <Label className="text-slate-300">{t("Nombre completo")}</Label>
            <Input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1.5 bg-[#0B0F1A] border-white/15 focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <Label className="text-slate-300">{t("Teléfono / WhatsApp")}</Label>
            <Input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1.5 bg-[#0B0F1A] border-white/15 focus:ring-2 focus:ring-blue-500" />
          </div>
          <Button data-testid="profile-save" type="submit" disabled={saving}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8">
            {saving ? t("Guardando...") : t("Guardar cambios")}
          </Button>
        </form>
      </div>

      <div className="card-tech rounded-xl p-8">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-5 h-5 text-[#22C55E]" strokeWidth={1.5} />
          <h3 className="text-lg font-semibold">{t("Documento de identidad")}</h3>
        </div>
        <p className="text-sm text-slate-400 mb-6">
          {t("Copart exige una identificación oficial (licencia de conducir o pasaporte) para poder pujar a tu nombre. Imagen o PDF, máx. 5MB.")}
        </p>
        {user?.has_id_document ? (
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span data-testid="id-status" className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5">
              {t("Documento en archivo")}
            </span>
            <a href={`${API}/profile/id-document`} target="_blank" rel="noreferrer"
              className="text-sm text-[#2563EB] hover:text-blue-400 inline-flex items-center gap-1.5 transition-colors" data-testid="id-view">
              <Eye className="w-4 h-4" strokeWidth={1.5} /> {t("Ver documento")}
            </a>
          </div>
        ) : (
          <p data-testid="id-status" className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5 inline-block mb-6">
            {t("Pendiente — súbelo para que podamos pujar por ti")}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Input data-testid="id-file-input" type="file" accept="image/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="bg-[#0B0F1A] border-white/15 max-w-xs file:text-slate-300" />
          <Button data-testid="id-upload-btn" onClick={upload} disabled={!file || uploading}
            className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-full">
            <FileUp className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {uploading ? t("Subiendo...") : t("Subir documento")}
          </Button>
        </div>
      </div>
    </div>
  );
}
